import { Popover } from '@timelyapp/react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { safeHttpHref } from '~/utils/url-helpers';
import type { LinkedWorkOption } from '../hooks/useLinkedWorkOptions';
import styles from './TimesheetProposal.LinkedWorkSelect.module.scss';

interface Props {
  children: React.ReactElement;
  openOnClick?: boolean;
  item: Pick<LinkedWorkOption, 'label'> &
    Partial<Pick<LinkedWorkOption, 'key' | 'toolName' | 'isCompleted' | 'description' | 'url'>>;
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export const LinkedWorkPreview = ({
  item,
  children,
  openOnClick = true,
  onOpenChange,
  onCloseAutoFocus,
}: Props) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const wrapper = useRef<HTMLSpanElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const clearTimer = useCallback(() => clearTimeout(timer.current), []);
  const changeOpen = useCallback(
    (next: boolean) => {
      clearTimer();
      onOpenChange?.(next);
      setOpen(next);
    },
    [clearTimer, onOpenChange]
  );
  const schedule = (next: boolean) => {
    clearTimer();
    timer.current = setTimeout(
      () => {
        if (
          !next &&
          (wrapper.current?.contains(document.activeElement) ||
            content.current?.contains(document.activeElement))
        )
          return;
        changeOpen(next);
      },
      next ? 250 : 200
    );
  };
  useEffect(
    () => () => {
      clearTimer();
      onOpenChange?.(false);
    },
    [clearTimer, onOpenChange]
  );
  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (content.current?.contains(document.activeElement))
        wrapper.current?.querySelector<HTMLElement>('button, [tabindex]')?.focus();
      changeOpen(false);
      onCloseAutoFocus?.(event);
    };
    const handleOutsidePointer = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !wrapper.current?.contains(event.target) &&
        !content.current?.contains(event.target)
      )
        changeOpen(false);
    };
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('pointerdown', handleOutsidePointer, true);
    return () => {
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('pointerdown', handleOutsidePointer, true);
    };
  }, [open, changeOpen, onCloseAutoFocus]);
  const url = safeHttpHref(item.url);
  const description = item.description?.trim().replace(/\s+/g, ' ');
  const excerpt =
    description && description.length > 240
      ? `${description.slice(0, 240).trimEnd()}…`
      : description;

  return (
    <span
      ref={wrapper}
      className={styles.previewTarget}
      data-linked-work-preview
      onMouseEnter={() => schedule(true)}
      onMouseLeave={() => schedule(false)}
      onFocusCapture={() => changeOpen(true)}
      onClick={event => {
        if (!content.current?.contains(event.target as Node)) changeOpen(openOnClick);
      }}
      onKeyDown={event => {
        if (event.key !== 'Tab' || !open) return;
        if (
          !event.shiftKey &&
          event.target instanceof Element &&
          event.target.closest('[data-linked-work-preview]')
        ) {
          const link = content.current?.querySelector('a');
          if (link) {
            event.preventDefault();
            link.focus();
          }
        } else if (event.shiftKey && content.current?.contains(event.target as Node)) {
          event.preventDefault();
          wrapper.current?.querySelector<HTMLElement>('button, [tabindex]')?.focus();
        }
      }}
      onBlur={event => {
        if (
          !wrapper.current?.contains(event.relatedTarget) &&
          !content.current?.contains(event.relatedTarget)
        ) {
          changeOpen(false);
        }
      }}
    >
      {children}
      <Popover
        isOpen={open}
        usePortal
        fixed
        position="right-start"
        width="min(360px, calc(100vw - 24px))"
        zIndex={2002}
        enableKeyTrap={false}
        closeOnOutsideClick={false}
        stopPropagation
        targetCaret={false}
        className="ignore-react-onclickoutside"
        targetWrapper={false}
        target={<span className={styles.previewAnchor} aria-hidden="true" />}
      >
        <div
          ref={content}
          role="dialog"
          aria-labelledby={titleId}
          className={styles.previewCard}
          onMouseEnter={clearTimer}
          onMouseLeave={() => schedule(false)}
          onClick={event => event.stopPropagation()}
        >
          <h3 id={titleId} className={styles.previewTitle}>
            {item.label}
          </h3>
          <div className={styles.previewMetadata}>
            {item.key && <strong>{item.key}</strong>}
            {item.toolName && <span>{item.toolName}</span>}
            {item.isCompleted != null && <span>{item.isCompleted ? 'Done' : 'Open'}</span>}
          </div>
          {excerpt && <p className={styles.previewDescription}>{excerpt}</p>}
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer">
              Open in {item.toolName || 'source'}
            </a>
          )}
        </div>
      </Popover>
    </span>
  );
};
